import { ActionType } from '../../../../core/redux/actionType';
import { DevPlanFieldAndAnswer } from '../../models';
import { makeApiAction, makeRsaaTypes, ApiReducer, makeApiReducer } from '../../../../core/api/rsaa';
import { Endpoints } from '../../../../core/api/endpoints';
import { AppState } from '../../../../core/redux/appState';
import { getLoading, addLoading, addError, noLoadingNoError } from '../../../../core/api/module';
import * as R from 'ramda';
import { actionFieldsById } from '../../devPlanAction/module';


const KEY_ACTION = ActionType.UpdateFieldAnswerListRequest;
const ACTIONS    = [
  ActionType.UpdateFieldAnswerListRequest,
  ActionType.UpdateFieldAnswerListResponse,
  ActionType.UpdateFieldAnswerListError,
];

type Payload = DevPlanFieldAndAnswer[];
type Meta = {
  projectId: number;
  participantId: number;
};


/**
 * Dispatch this to update the list of answers associated with the dev action list field
 *
 * @param projectId - the database Id for the project containing the dev plan
 * @param participantId - the database Id for the participant of the project containing the dev plan
 * @param languageId - the database Id for the language in which the answers were selected
 * @param devItemId - the database Id for the development item containing the dev action
 * @param devActionId - the database Id for the development action
 * @param devFieldId - the database Id for the field whose answers we're updating
 * @param devFieldAnswerId - the database Id for the dev field's answer list
 * @param compilationCategoryListIds - the database Ids for the answers which comprise the dev field's answer list
 */
export const updateFieldAnswerListAction = (
  projectId: number,
  participantId: number,
  languageId: number,
  devItemId: number,
  devActionId: number,
  devFieldId: number,
  devFieldAnswerId: number,
  compilationCategoryListIds: number[],
) => makeApiAction<Payload, Meta>(
  {
    method:   'POST',
    endpoint: Endpoints.UpdateFieldAnswerList,
    headers:  { 'content-type': 'application/json' },
    body:     JSON.stringify(
      {
        projectId,
        participantId,
        languageId,
        devItemId,
        devActionId,
        devFieldId,
        devFieldAnswerId,
        compilationCategoryListIds,
      },
    ),
    types:    makeRsaaTypes<Payload, Meta>(ACTIONS,{ projectId, participantId }),
    bailout:  (state: AppState) => !!getLoading(state, KEY_ACTION, `${projectId}-${participantId}`),
  },
);

const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const { projectId, participantId } = action.meta;

  const metaTag = `${projectId}-${participantId}`;

  if (isLoading) {
    return addLoading(state, KEY_ACTION, metaTag);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, metaTag);
  }

  const successState = noLoadingNoError(state, KEY_ACTION, metaTag);

  if (!payload) {
    return successState;
  }

  const actions = (payload || [])
    .map(fa => !!fa.devPlanFieldId ? fa : R.assoc('devPlanFieldId', fa.question, fa));

  return R.assocPath(['devPlan', 'fieldsAnswers'], actionFieldsById(actions), successState);
};

export const updateFieldAnswerListReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
